import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { GeofenceService } from "../../services/GeofenceService";

const normalizeOrgId = (orgId) => {
  if (orgId === undefined || orgId === null || orgId === "") {
    return null;
  }
  return String(orgId);
};

const getOrganisationId = (variables) => {
  return (
    variables?.orgId ??
    variables?.organisation_id ??
    variables?.data?.orgId ??
    variables?.data?.organisation_id ??
    null
  );
};

export const useGeofences = (orgId) => {
  const normalizedOrgId = normalizeOrgId(orgId);

  return useQuery({
    queryKey: ["geofences", normalizedOrgId],
    queryFn: () => GeofenceService.fetchGeofences(normalizedOrgId),
    enabled: !!normalizedOrgId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useGeofence = (geofenceId) => {
  return useQuery({
    queryKey: ["geofence", geofenceId],
    queryFn: () => GeofenceService.fetchGeofence(geofenceId),
    enabled: !!geofenceId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useCreateGeofence = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: GeofenceService.createGeofence,
    onSuccess: async (_, variables) => {
      const orgId = normalizeOrgId(getOrganisationId(variables));
      if (!orgId) return;

      await queryClient.invalidateQueries({ queryKey: ["geofences", orgId] });
      await queryClient.refetchQueries({
        queryKey: ["geofences", orgId],
        type: "all",
      });
    },
  });
};

export const useUpdateGeofence = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ geofenceId, data }) =>
      GeofenceService.updateGeofence(geofenceId, data),
    onSuccess: async (_, { geofenceId, data }) => {
      const orgId = normalizeOrgId(getOrganisationId(data));

      await queryClient.invalidateQueries({
        queryKey: ["geofence", geofenceId],
      });
      if (!orgId) return;

      await queryClient.invalidateQueries({ queryKey: ["geofences", orgId] });
      await queryClient.refetchQueries({
        queryKey: ["geofences", orgId],
        type: "all",
      });
    },
  });
};

export const useDeleteGeofence = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ geofenceId }) => GeofenceService.deleteGeofence(geofenceId),
    onSuccess: async (_, { geofenceId, orgId }) => {
      const normalizedOrgId = normalizeOrgId(orgId);

      if (normalizedOrgId) {
        await queryClient.invalidateQueries({
          queryKey: ["geofences", normalizedOrgId],
        });
        await queryClient.refetchQueries({
          queryKey: ["geofences", normalizedOrgId],
          type: "all",
        });
      }

      queryClient.removeQueries({ queryKey: ["geofence", geofenceId] });
    },
  });
};

export const useGeofencePresence = (geofenceId) => {
  return useQuery({
    queryKey: ["geofencePresence", geofenceId],
    queryFn: () => GeofenceService.fetchGeofencePresence(geofenceId),
    enabled: !!geofenceId,
    staleTime: 1000 * 30,
  });
};

export const useGeofencePresenceHistory = (
  geofenceId,
  skip = 0,
  limit = 100
) => {
  return useQuery({
    queryKey: ["geofencePresenceHistory", geofenceId, skip, limit],
    queryFn: () =>
      GeofenceService.fetchGeofencePresenceHistory(geofenceId, skip, limit),
    enabled: !!geofenceId,
    staleTime: 1000 * 30,
  });
};

export const useUpdateGeofenceStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ geofenceId, status }) =>
      GeofenceService.updateGeofenceStatus(geofenceId, status),
    onSuccess: async (_, { orgId }) => {
      const normalizedOrgId = normalizeOrgId(orgId);
      if (!normalizedOrgId) return;

      await queryClient.invalidateQueries({
        queryKey: ["geofences", normalizedOrgId],
      });
      await queryClient.refetchQueries({
        queryKey: ["geofences", normalizedOrgId],
        type: "all",
      });
    },
  });
};
